import React, { useEffect, useState } from 'react';
import { ChainSelectorModal } from 'components';
import {
    useConsentContractList,
    useReceiptList,
    useUserGroupList,
    useUserList,
    useWalletList,
} from '../../hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChainSvg } from '../../assets';
import {
    getColumns,
    getConsentContractSharedWithColumnData,
    getConsentContractStatus,
    TabItems,
    tableHeight,
    TabsItemsType,
} from './consent-contract-helper';
import { useAuth, usePermission } from '../../core';
import { ROLES } from '../../core/contexts/permissions';
import { ContractsSharedStyles } from 'lib';
import { ContractsPageWrapper } from 'components/contracts-page-wrapper/ContractsPageWrapper';
import { CONSENT_CONTRACT_PATHS } from 'constants/consent-contracts-paths';
import { GridSortModel } from '@mui/x-data-grid';

export const ConsentContracts = (): React.JSX.Element => {
    const { data: userList } = useUserList();
    const { hasPermissions } = usePermission();
    const navigate = useNavigate();
    const location = useLocation();
    const { getRoles } = useAuth();
    const isAdminRole: boolean = getRoles().includes(ROLES.admin);
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [rowsData, setRowsData] = useState<any>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
    const [isTabAndTableComponentVisible, setIsTabAndTableComponentVisible] =
        useState(false);
    const [selectedTab, setSelectedTab] = useState<TabsItemsType>('Contracts');
    const [selectedChain, setSelectedChain] = useState<string>('');
    const [addNewContractSelectedChain, setAddNewContractSelectedChain] =
        useState<string>('');
    const [openChainSelectorModal, setOpenChainSelectorModal] =
        useState<boolean>(false);
    const [selectChainFieldError, setSelectChainFieldError] =
        useState<boolean>(false);
    const {
        data: consentContractData,
        isLoading: consentContractDataLoading,
        refetch: refetchConsentContractData,
    } = useConsentContractList({
        chain: selectedChain,
        enabled: !!selectedChain,
    });
    const {
        data: receiptListData,
        isLoading: receiptListDataLoading,
        refetch: refetchReceiptListData,
    } = useReceiptList({
        chain: selectedChain,
        contractType: 'consentcontracts',
        receiptType: isAdminRole ? 'admin' : 'user',
        enabled: !!selectedChain,
    });
    const { data: walletsData, isLoading: walletsLoading } = useWalletList();
    const { data: userGroupsData, isLoading: userGroupsLoading } =
        useUserGroupList();
    const columns = getColumns(
        selectedTab,
        walletsData?.data?.userWallets ?? [],
        walletsData?.data?.systemWallets ?? []
    );

    useEffect(() => {
        if (location?.state) {
            setSelectedChain(location?.state);
        }
        if (tab) {
            setSelectedTab(tab as TabsItemsType);
        }
    }, [location?.state, tab]);

    useEffect(() => {
        if (selectedChain) {
            setIsDataLoading(true);
            setIsLoading(true);
            setIsTabAndTableComponentVisible(true);
            setRowsData([]);
        }
        if (
            selectedTab === 'Contracts' &&
            selectedChain &&
            !consentContractDataLoading
        ) {
            const rows = consentContractData?.map((item: any) => ({
                id: item.header?.id,
                dictionary: item.header?.dictionary,
                to:
                    item.consentContract?.to ??
                    item.consentContract?.jwtProperties ??
                    '',
                start: item.consentContract?.start,
                until: item.consentContract?.until,
                status: getConsentContractStatus(
                    item.consentContract?.start,
                    item.consentContract?.until
                ),
                shareWith: getConsentContractSharedWithColumnData(
                    item.consentContract?.to ??
                        item.consentContract?.jwtProperties ??
                        '',
                    userList?.data ?? [],
                    userGroupsData?.data ?? []
                ),
                timestamp: item.header?.timestamp,
            }));
            setRowsData(rows);
            setIsDataLoading(false);
        } else if (
            selectedTab === 'Receipts' &&
            selectedChain &&
            !receiptListDataLoading &&
            !walletsLoading
        ) {
            const rows = receiptListData?.data?.map((item) => ({
                id: item.contractSdoId,
                timestamp: item.header.timestamp,
                contractOwnerId: item.contractOwnerId,
                effectiveId: item.effectiveId,
                loggedInId: item.loggedInId,
                ...item,
            }));
            setRowsData(rows);
            setIsDataLoading(false);
        }
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        consentContractDataLoading,
        consentContractData,
        selectedChain,
        selectedTab,
        receiptListData,
        receiptListDataLoading,
        walletsLoading,
        userGroupsData,
        userGroupsLoading,
    ]);

    const handleRowClick = (row: any): void => {
        if (!selectedChain) return;
        if (selectedTab === 'Contracts') {
            navigate(
                `${CONSENT_CONTRACT_PATHS.LANDING_PAGE}/${row.row.dictionary}/${row.id}`
            );
        } else {
            const { id, timestamp, status, shareWith, ...receiptData } =
                row.row;
            navigate(
                `${CONSENT_CONTRACT_PATHS.LANDING_PAGE}/receipt/${selectedChain}/${row.id}`,
                {
                    state: receiptData,
                }
            );
        }
    };

    const handleAddConsentContract = (): void => {
        const permission = hasPermissions('createData');
        if (permission) {
            setOpenChainSelectorModal(true);
            if (selectedChain) {
                setAddNewContractSelectedChain(selectedChain);
            }
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handleCloseChainSelectorModal = (): void => {
        setOpenChainSelectorModal(false);
        setSelectChainFieldError(false);
        setAddNewContractSelectedChain('');
    };

    const handleNextButtonClick = (): void => {
        if (!addNewContractSelectedChain) {
            setSelectChainFieldError(true);
        } else {
            setOpenChainSelectorModal(false);
            setSelectChainFieldError(false);
            navigate(
                `${CONSENT_CONTRACT_PATHS.CREATE_NEW_CONSENT_CONTRACT}?chain=${addNewContractSelectedChain}`
            );
        }
    };

    const onTabChange = (event: React.SyntheticEvent, newValue: any): void => {
        if (event && selectedChain) {
            setSelectedTab(newValue);
            setRowsData([]);
            setIsLoading(true);
            if (newValue === 'Contracts') {
                refetchConsentContractData();
            } else if (newValue === 'Receipts') {
                refetchReceiptListData();
            } else {
                setRowsData([]);
            }
        }
    };

    const getSortModel = (): GridSortModel => {
        return [{ field: 'timestamp', sort: 'desc' }];
    };

    return (
        <>
            <ContractsPageWrapper
                title={'Consent Contracts'}
                handleAddContract={handleAddConsentContract}
                rowsData={rowsData}
                isLoading={
                    isDataLoading ||
                    isLoading ||
                    consentContractDataLoading ||
                    receiptListDataLoading
                }
                columns={columns}
                handleRowClick={handleRowClick}
                emptyStateIcon={ChainSvg}
                emptyStateBodyMessage={
                    selectedTab === 'Contracts'
                        ? 'Add a new consent contract using the button at the top right of the screen.'
                        : 'Select a different chain to view available receipts.'
                }
                tabItems={TabItems}
                onTabChange={onTabChange}
                selectedTab={selectedTab}
                tabsStyles={ContractsSharedStyles.tabsStyles}
                tabsContainerStyles={ContractsSharedStyles.tabsContainerStyles}
                tabItemStyles={ContractsSharedStyles.tabItemStyles}
                tableHeight={tableHeight}
                selectedChain={selectedChain}
                setSelectedChain={setSelectedChain}
                displayPermissionDialog={displayPermissionDialog}
                setDisplayPermissionDialog={setDisplayPermissionDialog}
                isTabAndTableComponentVisible={isTabAndTableComponentVisible}
                sortModel={getSortModel()}
            />
            {openChainSelectorModal && (
                <ChainSelectorModal
                    chainSelectorModal={openChainSelectorModal}
                    chainSelectorModalTitle='New Consent Contract'
                    chainSelectorModalSubheading={
                        'Select a chain to place your consent contract.'
                    }
                    handleCancelButtonClick={handleCloseChainSelectorModal}
                    handleCloseModal={handleCloseChainSelectorModal}
                    handleNextButtonClick={handleNextButtonClick}
                    selectedChain={addNewContractSelectedChain}
                    setSelectedChain={setAddNewContractSelectedChain}
                    selectChainFieldError={selectChainFieldError}
                    setSelectChainFieldError={setSelectChainFieldError}
                />
            )}
        </>
    );
};
